import React, { useEffect, useRef, useState } from 'react';
import styles from './jiraSettings.module.css';
import { Button, Col, Form, Input, message, Row, Select, Switch } from 'antd';
import { ExportOutlined, QuestionCircleFilled } from '@ant-design/icons';
import { get, post } from '../../utils/fetch';
import { useIntegrationConnection } from './useIntegrationConnection';

type OptionType = { value: string; label: string };

interface Props {
  projectId: string;
  organizationId: string;
  authProviders?: string[];
  integration:
    | {
        enabled: boolean;
        id: string;
        settings: {
          workspace: OptionType;
          project: OptionType;
          group: OptionType;
          status_column: OptionType;
          people_column: OptionType;
          new_tasks: boolean;
          sync_task_updates: boolean;
          new_comments: boolean;
          sync_monday_comments: boolean;
          sync_monday_status: boolean;
          task_status: OptionType;
          status_mapping: Record<string, string>;
        };
      }
    | undefined;
  onDisconnect: () => void;
}

const emptyOption = undefined as OptionType | undefined;

const MondaySettings: React.FC<Props> = ({
  projectId,
  organizationId,
  authProviders = [],
  integration,
  onDisconnect
}) => {
  const [form] = Form.useForm();
  const [authForm] = Form.useForm();
  const [isSavingAuth, setIsSavingAuth] = useState(false);
  const [authToken, setAuthToken] = useState('');
  const [selectedWorkspace, setSelectedWorkspace] = useState<
    OptionType | undefined
  >(integration?.settings?.workspace);
  const [selectedBoard, setSelectedBoard] = useState<OptionType | undefined>(
    integration?.settings?.project
  );
  const [selectedGroup, setSelectedGroup] = useState<OptionType | undefined>(
    integration?.settings?.group
  );
  const [selectedStatusColumn, setSelectedStatusColumn] = useState<
    OptionType | undefined
  >(integration?.settings?.status_column);
  const [selectedPeopleColumn, setSelectedPeopleColumn] = useState<
    OptionType | undefined
  >(integration?.settings?.people_column);
  const [selectedStatus, setSelectedStatus] = useState<OptionType | undefined>(
    integration?.settings?.task_status
  );
  const [autoPost, setAutoPost] = useState(
    integration?.settings?.new_tasks ?? false
  );
  const [syncTaskUpdates, setSyncTaskUpdates] = useState(
    integration?.settings?.sync_task_updates ?? true
  );
  const [newComments, setNewComments] = useState(
    integration?.settings?.new_comments ?? false
  );
  const [syncMondayComments, setSyncMondayComments] = useState(
    integration?.settings?.sync_monday_comments ?? false
  );
  const [syncMondayStatus, setSyncMondayStatus] = useState(
    integration?.settings?.sync_monday_status ?? false
  );
  const [statusMappings, setStatusMappings] = useState<Record<string, string>>(
    {}
  );
  const [workspaceOptions, setWorkspaceOptions] = useState<OptionType[]>([]);
  const [boardOptions, setBoardOptions] = useState<OptionType[]>([]);
  const [groupOptions, setGroupOptions] = useState<OptionType[]>([]);
  const [statusColumnOptions, setStatusColumnOptions] = useState<OptionType[]>(
    []
  );
  const [peopleColumnOptions, setPeopleColumnOptions] = useState<OptionType[]>(
    []
  );
  const [bugherdStatusOptions, setBugherdStatusOptions] = useState<
    OptionType[]
  >([]);
  const [mondayStatusOptions, setMondayStatusOptions] = useState<OptionType[]>(
    []
  );
  const [isWorkspacesLoading, setIsWorkspacesLoading] = useState(false);
  const [isBoardsLoading, setIsBoardsLoading] = useState(false);
  const [isFindingBoard, setIsFindingBoard] = useState(false);
  const [isGroupsLoading, setIsGroupsLoading] = useState(false);
  const [isColumnsLoading, setIsColumnsLoading] = useState(false);
  const [isStatusLoading, setIsStatusLoading] = useState(false);
  const [isMondayStatusLoading, setIsMondayStatusLoading] = useState(false);
  const [lastBoardLookupId, setLastBoardLookupId] = useState('');
  const boardLookupTimeout = useRef<number | undefined>();

  const invertStatusMappings = (mapping: Record<string, string> = {}) =>
    Object.entries(mapping).reduce((acc, [mondayStatusId, bugherdColumnId]) => {
      acc[bugherdColumnId] = mondayStatusId;
      return acc;
    }, {} as Record<string, string>);

  useEffect(() => {
    if (integration?.settings?.status_mapping) {
      setStatusMappings(
        invertStatusMappings(integration.settings.status_mapping)
      );
    }
  }, [integration]);

  useEffect(
    () => () => {
      window.clearTimeout(boardLookupTimeout.current);
    },
    []
  );

  const hasMondayAuth = authProviders.includes('monday') || !!integration?.enabled;
  const connectionIntegration =
    integration || (hasMondayAuth ? { enabled: true } : undefined);
  const {
    integrationId,
    projectIntegrationId,
    getConnectedState,
    saveSettings,
    disconnect
  } = useIntegrationConnection({
    organizationId,
    projectId,
    integration: connectionIntegration,
    integrationCode: 'Monday',
    onDisconnect,
    onConnectedStateLoaded: results => {
      if (!results.settings) return;
      setSelectedWorkspace(results.settings.workspace);
      setSelectedBoard(results.settings.project);
      setSelectedGroup(results.settings.group);
      setSelectedStatusColumn(results.settings.status_column);
      setSelectedPeopleColumn(results.settings.people_column);
      setSelectedStatus(results.settings.task_status);
      setAutoPost(results.settings.new_tasks ?? false);
      setSyncTaskUpdates(results.settings.sync_task_updates ?? true);
      setNewComments(results.settings.new_comments ?? false);
      setSyncMondayComments(results.settings.sync_monday_comments ?? false);
      setSyncMondayStatus(results.settings.sync_monday_status ?? false);
      setStatusMappings(invertStatusMappings(results.settings.status_mapping));
      form.setFieldsValue({
        workspace: results.settings.workspace?.value,
        project: results.settings.project?.value,
        group: results.settings.group?.value,
        statusColumn: results.settings.status_column?.value,
        peopleColumn: results.settings.people_column?.value,
        bugherdStatus: results.settings.task_status?.value
      });
    }
  });
  const canLoadMondayData = hasMondayAuth && !!integrationId;

  const saveAuth = async (values: { auth_token: string }) => {
    setIsSavingAuth(true);
    try {
      await post(`/organizations/${organizationId}/integrations/monday_auth`, {
        project_id: projectId,
        auth_token: values.auth_token
      });
      message.success('Monday connected.');
      window.location.href = `${window.location.pathname}?open_integration=monday`;
    } catch (error) {
      message.error('Failed to connect Monday. Please check your API token.');
    } finally {
      setIsSavingAuth(false);
    }
  };

  const getProjectData = async () => {
    if (!hasMondayAuth) return;
    setIsStatusLoading(true);
    try {
      const results = await get(`/projects/full?id=${projectId}`);
      setBugherdStatusOptions(
        results.columns.map(({ id, name }: { id: string; name: string }) => ({
          value: String(id),
          label: name
        }))
      );
    } catch (error) {
      message.error(
        'Failed to fetch BugHerd project details. Please try again.'
      );
    } finally {
      setIsStatusLoading(false);
    }
  };

  const getWorkspaces = async () => {
    if (!canLoadMondayData) return;
    setIsWorkspacesLoading(true);
    try {
      const results = await get(
        `/organizations/${organizationId}/integrations/remote_projects?id=${integrationId}&site_name=workspaces`
      );
      setWorkspaceOptions(results);
    } catch (error) {
      message.error('Failed to fetch Monday workspaces. Please try again.');
    } finally {
      setIsWorkspacesLoading(false);
    }
  };

  const getBoards = async (workspaceId: string) => {
    if (!canLoadMondayData || !workspaceId) return;
    setIsBoardsLoading(true);
    try {
      const results = await get(
        `/organizations/${organizationId}/integrations/remote_projects?id=${integrationId}&site_name=boards:${workspaceId}`
      );
      setBoardOptions(mergeSelectedBoard(results));
    } catch (error) {
      message.error('Failed to fetch Monday boards. Please try again.');
    } finally {
      setIsBoardsLoading(false);
    }
  };

  const getBoardData = async (boardId: string) => {
    if (!canLoadMondayData || !boardId) return;
    setIsGroupsLoading(true);
    setIsColumnsLoading(true);
    try {
      const [groups, statusColumns, peopleColumns] = await Promise.all([
        get(
          `/organizations/${organizationId}/integrations/remote_projects?id=${integrationId}&site_name=groups:${boardId}`
        ),
        get(
          `/organizations/${organizationId}/integrations/remote_projects?id=${integrationId}&site_name=columns:${boardId}`
        ),
        get(
          `/organizations/${organizationId}/integrations/remote_projects?id=${integrationId}&site_name=people_columns:${boardId}`
        )
      ]);
      setGroupOptions(groups);
      setStatusColumnOptions(statusColumns);
      setPeopleColumnOptions(peopleColumns);
    } catch (error) {
      message.error('Failed to fetch Monday board details. Please try again.');
    } finally {
      setIsGroupsLoading(false);
      setIsColumnsLoading(false);
    }
  };

  const getMondayStatuses = async (boardId: string, columnId: string) => {
    if (!canLoadMondayData || !boardId || !columnId) return;
    setIsMondayStatusLoading(true);
    try {
      const results = await get(
        `/organizations/${organizationId}/integrations/monday_statuses?id=${integrationId}&board_id=${boardId}&column_id=${columnId}`
      );
      setMondayStatusOptions(results);
    } catch (error) {
      message.error('Failed to fetch Monday statuses. Please try again.');
    } finally {
      setIsMondayStatusLoading(false);
    }
  };

  const mergeSelectedBoard = (options: OptionType[]) => {
    if (!selectedBoard?.value) return options;
    if (options.some(option => option.value === selectedBoard.value)) {
      return options;
    }

    return [...options, selectedBoard].sort((a, b) =>
      a.label.localeCompare(b.label)
    );
  };

  const addBoardOption = (board: OptionType) => {
    setBoardOptions(options => {
      const nextOptions = options.some(option => option.value === board.value)
        ? options.map(option => (option.value === board.value ? board : option))
        : [...options, board];

      return nextOptions.sort((a, b) => a.label.localeCompare(b.label));
    });
  };

  const extractBoardId = (value: string) => {
    const trimmedValue = value.trim();
    if (/^\d+$/.test(trimmedValue)) return trimmedValue;

    const boardPathMatch = trimmedValue.match(/boards\/(\d+)/);
    if (boardPathMatch) return boardPathMatch[1];

    return trimmedValue.match(/\d{6,}/)?.[0] || '';
  };

  const findBoardById = async (boardId: string) => {
    if (!canLoadMondayData) return;

    if (
      !boardId ||
      boardId === lastBoardLookupId ||
      boardOptions.some(option => option.value === boardId)
    ) {
      return;
    }

    setLastBoardLookupId(boardId);
    setIsFindingBoard(true);
    try {
      const results = await get(
        `/organizations/${organizationId}/integrations/remote_projects?id=${integrationId}&site_name=board:${encodeURIComponent(
          boardId
        )}`
      );
      const board = results[0];
      if (!board) {
        return;
      }

      addBoardOption(board);
    } catch (error) {
      message.error('Failed to fetch Monday board. Please try again.');
    } finally {
      setIsFindingBoard(false);
    }
  };

  const handleBoardSearch = (value: string) => {
    const boardId = extractBoardId(value);
    if (!boardId) return;

    window.clearTimeout(boardLookupTimeout.current);
    boardLookupTimeout.current = window.setTimeout(() => {
      findBoardById(boardId);
    }, 300);
  };

  const filterBoardOption = (
    inputValue: string,
    option?: OptionType
  ) =>
    [option?.label, option?.value].some(value =>
      value?.toLowerCase().includes(inputValue.toLowerCase())
    );

  const asOption = (option: OptionType | OptionType[] | undefined) =>
    Array.isArray(option) ? option[0] : option;

  const handleWorkspaceChange = (
    _value: string | undefined,
    option: OptionType | OptionType[] | undefined
  ) => {
    setSelectedWorkspace(asOption(option));
    setSelectedBoard(emptyOption);
    setSelectedGroup(emptyOption);
    setSelectedStatusColumn(emptyOption);
    setSelectedPeopleColumn(emptyOption);
    setStatusMappings({});
    setLastBoardLookupId('');
    form.setFieldsValue({
      project: undefined,
      group: undefined,
      statusColumn: undefined,
      peopleColumn: undefined
    });
  };

  const handleBoardChange = (
    _value: string | undefined,
    option: OptionType | OptionType[] | undefined
  ) => {
    setSelectedBoard(asOption(option));
    setSelectedGroup(emptyOption);
    setSelectedStatusColumn(emptyOption);
    setSelectedPeopleColumn(emptyOption);
    setStatusMappings({});
    form.setFieldsValue({
      group: undefined,
      statusColumn: undefined,
      peopleColumn: undefined
    });
  };

  const handleGroupChange = (
    _value: string | undefined,
    option: OptionType | OptionType[] | undefined
  ) => {
    setSelectedGroup(asOption(option));
  };

  const handleStatusColumnChange = (
    _value: string | undefined,
    option: OptionType | OptionType[] | undefined
  ) => {
    setSelectedStatusColumn(asOption(option));
    setStatusMappings({});
  };

  const handlePeopleColumnChange = (
    _value: string | undefined,
    option: OptionType | OptionType[] | undefined
  ) => {
    setSelectedPeopleColumn(asOption(option));
  };

  const handleStatusChange = (
    _value: string | undefined,
    option: OptionType | OptionType[] | undefined
  ) => {
    setSelectedStatus(asOption(option));
  };

  const handleStatusMappingChange = (
    bugherdColumnId: string,
    mondayStatusId?: string
  ) => {
    setStatusMappings({
      ...statusMappings,
      [bugherdColumnId]: mondayStatusId || ''
    });
  };

  const handleSubmit = () => {
    const validBugherdColumnIds = bugherdStatusOptions.map(
      option => option.value
    );
    const cleanedStatusMappings = Object.keys(statusMappings).reduce(
      (acc, bugherdColumnId) => {
        const mondayStatusId = statusMappings[bugherdColumnId];
        if (!mondayStatusId) return acc;
        if (!validBugherdColumnIds.includes(bugherdColumnId)) return acc;
        acc[mondayStatusId] = bugherdColumnId;
        return acc;
      },
      {} as Record<string, string>
    );

    return saveSettings(
      {
        workspace: selectedWorkspace,
        project: selectedBoard,
        group: selectedGroup || {},
        status_column: selectedStatusColumn || {},
        people_column: selectedPeopleColumn || {},
        new_tasks: autoPost,
        sync_task_updates: syncTaskUpdates,
        new_comments: newComments,
        sync_monday_comments: syncMondayComments,
        sync_monday_status: syncMondayStatus,
        task_status: selectedStatus,
        status_mapping: cleanedStatusMappings
      },
      { form }
    );
  };

  useEffect(() => {
    if (hasMondayAuth) {
      getConnectedState({ errorMessage: false });
      getProjectData();
    }
  }, [integration, projectId, organizationId, hasMondayAuth]);

  useEffect(() => {
    if (canLoadMondayData) {
      getWorkspaces();
    }
  }, [integrationId, projectId, organizationId, hasMondayAuth]);

  useEffect(() => {
    if (selectedWorkspace?.value) {
      getBoards(selectedWorkspace.value);
    }
  }, [selectedWorkspace?.value]);

  useEffect(() => {
    if (selectedBoard?.value) {
      getBoardData(selectedBoard.value);
    }
  }, [selectedBoard?.value]);

  useEffect(() => {
    if (selectedBoard?.value && selectedStatusColumn?.value) {
      getMondayStatuses(selectedBoard.value, selectedStatusColumn.value);
    }
  }, [selectedBoard?.value, selectedStatusColumn?.value]);

  const renderStatusMappings = () => (
    <div className={styles.statusMappings}>
      <Row className={styles.statusMappingRow} gutter={16}>
        <Col span={11}>
          <strong>BugHerd Status</strong>
        </Col>
        <Col span={2} />
        <Col span={11}>
          <strong>Monday Status</strong>
        </Col>
      </Row>
      {bugherdStatusOptions.map(bugherdStatus => (
        <Row
          key={bugherdStatus.value}
          className={styles.statusMappingRow}
          gutter={16}
          align="middle"
        >
          <Col span={11}>
            <span className={styles.jiraStatusLabel}>
              {bugherdStatus.label}
            </span>
          </Col>
          <Col span={2} style={{ textAlign: 'center' }}>
            <span>↔</span>
          </Col>
          <Col span={11}>
            <Select
              className={styles.bugherdColumnSelect}
              placeholder="Select Monday status"
              value={statusMappings[bugherdStatus.value] || undefined}
              onChange={value =>
                handleStatusMappingChange(bugherdStatus.value, value)
              }
              options={mondayStatusOptions}
              loading={isMondayStatusLoading}
              allowClear
            />
          </Col>
        </Row>
      ))}
    </div>
  );

  const renderIntegrationSettings = () => (
    <div>
      <Form
        form={form}
        layout="horizontal"
        onFinish={handleSubmit}
        autoComplete="off"
        requiredMark={false}
      >
        <div className={styles.jiraRequired}>
          <div className={styles.requiredBubble}>REQUIRED</div>
          <h3>Connect your Monday board</h3>
          <Row className={styles.jiraEditRow}>
            <Col span={24}>
              <Form.Item
                name="workspace"
                label={
                  <span className={styles.requiredLabel}>
                    <span>*</span>Monday Workspace
                  </span>
                }
                className={styles.jiraProject}
                rules={[
                  {
                    required: true,
                    message: 'Please select a Monday workspace.'
                  }
                ]}
                initialValue={selectedWorkspace?.value}
              >
                <Select
                  className={styles.jiraSelect}
                  showSearch
                  placeholder="Select a workspace"
                  optionFilterProp="label"
                  options={workspaceOptions}
                  onChange={handleWorkspaceChange}
                  loading={isWorkspacesLoading}
                  allowClear
                />
              </Form.Item>
            </Col>
          </Row>
          {selectedWorkspace?.value && (
            <Row className={styles.jiraEditRow}>
              <Col span={24}>
                <Form.Item
                  name="project"
                  label={
                    <span className={styles.requiredLabel}>
                      <span>*</span>Monday Board
                    </span>
                  }
                  className={styles.jiraProject}
                  rules={[
                    { required: true, message: 'Please select a Monday board.' }
                  ]}
                  initialValue={selectedBoard?.value}
                  tooltip="Search by board name, board ID, or paste a Monday board URL."
                >
                  <Select
                    className={styles.jiraSelect}
                    showSearch
                    placeholder="Select a board"
                    filterOption={filterBoardOption}
                    options={boardOptions}
                    onChange={handleBoardChange}
                    onSearch={handleBoardSearch}
                    notFoundContent={
                      isFindingBoard ? 'Searching by board ID...' : undefined
                    }
                    loading={isBoardsLoading || isFindingBoard}
                    allowClear
                  />
                </Form.Item>
              </Col>
            </Row>
          )}
          {selectedBoard?.value && (
            <Row className={styles.jiraEditRow}>
              <Col span={24}>
                <Form.Item
                  name="group"
                  label="Monday Group"
                  className={styles.jiraProject}
                  initialValue={selectedGroup?.value}
                >
                  <Select
                    className={styles.jiraSelect}
                    showSearch
                    placeholder="Select a group (optional)"
                    optionFilterProp="label"
                    options={groupOptions}
                    onChange={handleGroupChange}
                    loading={isGroupsLoading}
                    allowClear
                  />
                </Form.Item>
              </Col>
            </Row>
          )}
        </div>

        <div className={styles.jiraWorkflows}>
          <h3>Choose your desired workflow</h3>
          <Row className={styles.jiraEditRow}>
            <Col span={24}>
              <Form.Item
                label="Automatically post new tasks to Monday"
                className={styles.autoPost}
              >
                <Switch
                  checked={autoPost}
                  onChange={checked => setAutoPost(checked)}
                />
              </Form.Item>
            </Col>
            {autoPost && (
              <Col span={24}>
                <Form.Item
                  name="bugherdStatus"
                  label="Create task when status is equal to"
                  className={styles.selectStatus}
                  initialValue={selectedStatus?.value}
                  tooltip="This is an optional field. Select a status to trigger task creation in Monday."
                >
                  <Select
                    placeholder="Select a status"
                    options={bugherdStatusOptions}
                    onChange={handleStatusChange}
                    loading={isStatusLoading}
                    allowClear
                  />
                </Form.Item>
              </Col>
            )}
            <Col span={24}>
              <Form.Item label="Automatically sync task detail updates to Monday">
                <Switch
                  checked={syncTaskUpdates}
                  onChange={setSyncTaskUpdates}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row className={styles.jiraEditRow}>
            <Col span={24}>
              <Form.Item
                className={styles.newComments}
                label="Automatically send new BugHerd comments to Monday"
              >
                <Switch
                  checked={newComments}
                  onChange={checked => setNewComments(checked)}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row className={styles.jiraEditRow}>
            <Col span={24}>
              <Form.Item
                className={styles.syncComments}
                label="Sync comments from Monday to BugHerd"
              >
                <Switch
                  checked={syncMondayComments}
                  onChange={checked => setSyncMondayComments(checked)}
                  disabled={!selectedBoard?.value}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row className={styles.jiraEditRow}>
            <Col span={24}>
              <Form.Item
                className={styles.syncStatus}
                label="Sync status updates from Monday to BugHerd"
              >
                <Switch
                  checked={syncMondayStatus}
                  onChange={checked => setSyncMondayStatus(checked)}
                  disabled={!selectedBoard?.value}
                />
              </Form.Item>
            </Col>
          </Row>
          {(syncMondayStatus || autoPost) && selectedBoard?.value && (
            <>
              <Row className={styles.jiraEditRow}>
                <Col span={24}>
                  <Form.Item
                    name="statusColumn"
                    label="Monday status column"
                    className={styles.jiraProject}
                    initialValue={selectedStatusColumn?.value}
                    tooltip="The Monday status column to read from and write to when syncing statuses."
                  >
                    <Select
                      className={styles.jiraSelect}
                      showSearch
                      placeholder="Select a status column"
                      optionFilterProp="label"
                      options={statusColumnOptions}
                      onChange={handleStatusColumnChange}
                      loading={isColumnsLoading}
                      allowClear
                    />
                  </Form.Item>
                </Col>
              </Row>
              {selectedStatusColumn?.value && (
                <div className={styles.jiraEditRow}>
                  {renderStatusMappings()}
                </div>
              )}
            </>
          )}
          <Row className={styles.jiraEditRow}>
            <Col span={24}>
              <Form.Item
                name="peopleColumn"
                label="Monday people column"
                className={styles.jiraProject}
                initialValue={selectedPeopleColumn?.value}
                tooltip="The Monday people column to assign mapped users to."
              >
                <Select
                  className={styles.jiraSelect}
                  showSearch
                  placeholder="Select a people column (optional)"
                  optionFilterProp="label"
                  options={peopleColumnOptions}
                  onChange={handlePeopleColumnChange}
                  loading={isColumnsLoading}
                  disabled={!selectedBoard?.value}
                  allowClear
                />
              </Form.Item>
            </Col>
          </Row>
          <Row className={styles.jiraEditRow}>
            <Col span={24}>
              <Form.Item
                className={styles.syncUsers}
                label="Map assigned users between Monday and BugHerd"
              >
                <a
                  href={
                    projectIntegrationId && selectedBoard?.value
                      ? `/settings/organizations/${organizationId}/team?manage_monday_users=true&integration_id=${integration?.id}&project_integration_id=${projectIntegrationId}&board_id=${selectedBoard.value}`
                      : '#'
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.manageLink}
                  onClick={event => {
                    if (!projectIntegrationId || !selectedBoard?.value) {
                      event.preventDefault();
                      message.warning(
                        'Please save a Monday board before managing assigned user mapping.'
                      );
                    }
                  }}
                >
                  Manage <ExportOutlined style={{ marginLeft: '5px' }} />
                </a>
              </Form.Item>
            </Col>
          </Row>
        </div>

        <Row
          className={styles.jiraEditRow}
          justify="space-between"
          align="middle"
        >
          <Col>
            <Form.Item style={{ marginBottom: 0 }}>
              <Button
                type="primary"
                shape="round"
                htmlType="submit"
                className={styles.save}
              >
                Save
              </Button>
            </Form.Item>
          </Col>
          <Col>
            <Button
              type="text"
              shape="round"
              onClick={() => disconnect({ deleteFallback: true })}
              className={styles.disconnectButton}
              danger
            >
              Disconnect
            </Button>
          </Col>
        </Row>
      </Form>
    </div>
  );

  const renderAuthForm = () => (
    <div className={styles.jiraExplanation}>
      <Form
        form={authForm}
        layout="vertical"
        onFinish={saveAuth}
        requiredMark={false}
      >
        <Form.Item
          label="API V2 Token"
          name="auth_token"
          rules={[{ required: true, message: 'Enter your Monday API token.' }]}
        >
          <Input.Password
            autoComplete="off"
            onChange={event => setAuthToken(event.target.value)}
          />
        </Form.Item>
        <div className={styles.mondayConnectButton}>
          <Button
            type="primary"
            htmlType="submit"
            loading={isSavingAuth}
            disabled={!authToken.trim()}
          >
            Connect Monday account
          </Button>
        </div>
      </Form>
      <div className={styles.mondayTokenInstructions}>
        <h3>
          <QuestionCircleFilled className={styles.mondayHelpIcon} />
          How to find your Monday API token
        </h3>
        <ol>
          <li>
            Log into your{' '}
            <a
              href="https://monday.com"
              target="_blank"
              rel="noopener noreferrer"
            >
              monday.com
            </a>{' '}
            account
          </li>
          <li>Click on your avatar (picture icon) in the top right corner</li>
          <li>
            Select Administration from the menu (this requires you to have
            administration permissions)
          </li>
          <li>Go to Connections &gt; Personal API Token</li>
          <li>Copy your token</li>
        </ol>
      </div>
    </div>
  );

  return (
    <div className={styles.container}>
      {hasMondayAuth ? renderIntegrationSettings() : renderAuthForm()}
    </div>
  );
};

export default MondaySettings;
